<script setup lang="ts">
import { computed, ref, useSlots, watch } from "vue";
import VStack from "@magnit/core/src/components/VStack/VStack.vue";
import VStackItem from "@magnit/core/src/components/VStack/VStackItem.vue";
import VPagination from "@magnit/core/src/containers/VPagination/VPagination.vue";
import { useDebounceFn } from "@vueuse/core";
import { vIntersectionObserver } from "@vueuse/components";
import { useRoute } from "vue-router";
import { useAnalyticManager } from "@magnit/analytic-events";
import VProductPreview from "./VProductPreview.vue";
import VProductSkeleton from "./VProductSkeleton.vue";
import VCatalogEmpty from "./VCatalogEmpty.vue";
import type {
  IBasketAction,
  ICatalogListingEmits,
  ICatalogListingProps,
  IProduct,
  IProductPreviewProps,
} from "@magnit/unit-catalog/src/unit-catalog.types";
import type { ICatalogEmptyProps } from "../types/VCatalogEmpty.types";

const THROTTLE_TIMEOUT = 600;

const props = withDefaults(defineProps<ICatalogListingProps>(), {
  yaSlotPosition: 5,
  loadingItemsCount: 36,
  pagination: () => ({}),
  itemSize: () => ({ xs: 6, s: 6, m: 4, ml: 3, xl: 2 }),
  analyticsPayload: () => ({}),
  theme: "base",
  favoritesLoading: () => ({}),
  basketQty: () => ({}),
});
const emit = defineEmits<ICatalogListingEmits>();
const route = useRoute();
const { send: nativeSend } = useAnalyticManager();
const send = props.analyticsSender || nativeSend;

const slots = useSlots();

const showedIds = ref<string[]>([]);
const sentIds = ref<string[]>([]);
const sort = computed(() =>
  route?.query?.sortBy
    ? `sortBy=${route.query.sortBy}&order=${route.query.order}`
    : "",
);
const showCatalog = computed(() => props.loading || props.items.length > 0);
const onPaginationSetPage = (page: number) => {
  send("CatalogListing:Pagination:Click", {
    pageNumber: page,
    pageNum: `$${page}`,
    ...props.analyticsPayload,
  });
  emit("pagination:set-page", page);
};
const onPaginationShowMore = () => {
  send("CatalogListing:ShowMore:Click", {
    ...props.analyticsPayload,
  });
  emit("pagination:show-more");
};

const onSkuClick = (product: IProduct, index: number) => {
  emit("product:click", product);
  send("CatalogListing:Item:Click", {
    pageNumber: props.pagination.page,
    sort: sort.value,
    name: product.title,
    ...props.analyticsPayload,
    ...product,
    pos: index + 1,
  });
};

const onSkuVisibility = (
  item: IProduct,
  [{ isIntersecting }]: IntersectionObserverEntry[],
  index: number,
) => {
  if (isIntersecting) {
    send("CatalogListing:Item:View", {
      ...props.analyticsPayload,
      ...item,
      pos: index + 1,
    });
  }
  if (isIntersecting && !showedIds.value.includes(item.id)) {
    showedIds.value.push(item.id);
  }
};

const onShowMoreVisibility = ([
  { isIntersecting },
]: IntersectionObserverEntry[]) => {
  if (!isIntersecting) return;
  send("CatalogListing:ShowMore:View", {
    ...props.analyticsPayload,
  });
};

const debounceSendAnalyticsListView = useDebounceFn((items: IProduct[]) => {
  if (!items.length) return;
  const ids = items.map((i) => i.id);
  sentIds.value.push(...ids);
  send("CatalogListing:List:View", {
    pageNumber: props.pagination.page,
    list_id: items.map((product, index) => ({
      list_number: props.pagination.page,
      position: index + 1,
      name: product.title,
      product_id: product.id,
    })),
    ...props.analyticsPayload,
  });
}, THROTTLE_TIMEOUT);

const isYaSlotVisible = (index: number, name = "default") => {
  let pageSize = props.pagination?.pageSize ?? props.loadingItemsCount ?? 36;
  if (!!slots.default && props.yaSlotPosition) pageSize -= 1;
  if (!!slots.doubleSlotBanner && props.yaSlotDoublePosition) pageSize -= 2;
  const idx = index % pageSize;
  if (name === "default") {
    const offset =
      props.yaSlotDoublePosition !== undefined &&
      !!slots.doubleSlotBanner &&
      props.yaSlotDoublePosition < props.yaSlotPosition
        ? 3
        : 1;
    return idx === props.yaSlotPosition - offset && !!slots.default;
  } else if (name === "double" && props.yaSlotDoublePosition !== undefined) {
    const offset =
      !!slots.default && props.yaSlotPosition < props.yaSlotDoublePosition
        ? 2
        : 1;
    return (
      idx === props.yaSlotDoublePosition - offset && !!slots.doubleSlotBanner
    );
  }
  return false;
};

const isYaSlotWhenItemsUnderflow = computed(() => {
  const itemsLength = props.loading
    ? props.loadingItemsCount
    : props.items.length;

  return props.yaSlotPosition > itemsLength;
});

const doubleSlotSize = computed(() => {
  const keys = Object.keys(props.itemSize);
  const result: Record<string, number> = {};
  for (let i = 0, len = keys.length; i < len; i += 1) {
    const key = keys[i];
    result[key] = (props.itemSize as Record<string, number>)[key] * 2;
  }
  return result as ICatalogListingProps["itemSize"];
});

const emptyCatalogProps = computed(
  (): ICatalogEmptyProps => ({
    ...props.catalogEmpty,
    urlPrimaryButton:
      props.emptyUrlPrimaryButton ?? props.catalogEmpty?.urlPrimaryButton,
    urlSecondaryButton:
      props.emptyUrlSecondaryButton ?? props.catalogEmpty?.urlSecondaryButton,
  }),
);

const onFavoriteSet = (id: string, flag: boolean) =>
  emit("favorite:set", id, flag);

const onRatingClick = (id: string) => emit("rating:click", id);
const onBasket = (id: string, step: number, action: IBasketAction): void => {
  emit("basket", id, step, action);
};

watch(
  showedIds,
  (next) => {
    const items = props.items.filter(
      (item) => next.includes(item.id) && !sentIds.value.includes(item.id),
    );
    debounceSendAnalyticsListView(items as IProduct[]);
  },
  { deep: true },
);
</script>

<template>
  <div v-if="showCatalog" class="unit-catalog" data-test-id="unit-catalog">
    <VStack direction="row" align="stretch" :gap="{ xs: '12px', m: '20px' }">
      <template v-if="loading">
        <VStackItem
          v-for="idx in Number(props.loadingItemsCount)"
          :key="`skeleton-${idx}`"
          :size="itemSize"
          class="unit-catalog__stack-item"
        >
          <slot v-if="isYaSlotVisible(idx - 1)" />
          <slot v-else-if="isYaSlotVisible(idx - 1, 'double')" />
          <VProductSkeleton v-else />
        </VStackItem>
        <VStackItem
          v-if="isYaSlotWhenItemsUnderflow"
          :size="itemSize"
          class="unit-catalog__stack-item"
        >
          <slot />
        </VStackItem>
      </template>
      <template v-else>
        <template v-for="(item, idx) in items" :key="item.id">
          <VStackItem
            v-if="isYaSlotVisible(idx)"
            :size="itemSize"
            class="unit-catalog__stack-item"
          >
            <slot />
          </VStackItem>
          <VStackItem
            v-if="isYaSlotVisible(idx, 'double')"
            :size="doubleSlotSize"
            class="unit-catalog__stack-item"
          >
            <slot name="doubleSlotBanner" />
          </VStackItem>
          <VStackItem :size="itemSize" class="unit-catalog__stack-item">
            <slot
              name="productCard"
              :item="item"
              :on-sku-visibility="onSkuVisibility"
              :on-sku-click="onSkuClick"
            >
              <VProductPreview
                v-intersection-observer="
                  (io: IntersectionObserverEntry[]) =>
                    onSkuVisibility(item as IProduct, io, idx)
                "
                v-bind="item as IProductPreviewProps"
                :class="{ 'show-ratings': showRatings }"
                :theme="theme"
                :ecom="ecom"
                :favorites-available="favoritesAvailable"
                :favorites-loading="favoritesLoading[item.id]"
                :basket-qty="basketQty[item.id] || 0"
                @basket="onBasket"
                @favorite:set="onFavoriteSet"
                @rating:click="onRatingClick"
                @click="() => onSkuClick(item, idx)"
              />
            </slot>
          </VStackItem>
        </template>
        <VStackItem
          v-if="isYaSlotWhenItemsUnderflow"
          :size="itemSize"
          class="unit-catalog__stack-item"
        >
          <slot />
        </VStackItem>
      </template>
    </VStack>
    <VPagination
      v-intersection-observer="onShowMoreVisibility"
      v-bind="pagination"
      data-test-id="pagination-show-more"
      @pagination:set-page="onPaginationSetPage"
      @pagination:show-more="onPaginationShowMore"
    />
  </div>
  <div v-else class="unit-catalog-empty-wrap">
    <VCatalogEmpty
      v-bind="emptyCatalogProps"
      @click:primary="emit('empty:click:primary')"
      @click:secondary="emit('empty:click:secondary')"
    />
  </div>
</template>

<style lang="postcss" scoped>
.unit-catalog {
  display: flex;
  flex-direction: column;
  gap: var(--pl-unit-x3);

  @media (--pl-viewport-from-m) {
    gap: var(--pl-unit-x5);
  }

  &__stack-item {
    margin-bottom: var(--pl-unit-x1);
  }

  &-empty-wrap {
    display: flex;
    height: 100%;
    align-items: center;
    flex-grow: 1;
  }
}
</style>
